import React, { useState, useEffect, useRef } from 'react';
import { Upload, Trash2, Loader2, ArrowLeft, ArrowRight, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { PhotoSendersPanel } from '../../components/admin/PhotoSendersPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const thumb = (url) => url.replace('/upload/', '/upload/w_400,h_300,c_fill,q_auto,f_auto/');

const AdminCustomerPhotos = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(0);
  const inputRef = useRef(null);

  const load = async () => {
    const res = await axios.get(`${API}/customer-photos`);
    setPhotos(res.data.photos);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onFiles = async (e) => {
    const files = [...(e.target.files || [])].filter((f) => f.type.startsWith('image/'));
    e.target.value = '';
    let done = 0;
    for (const file of files) {
      setUploading(files.length - done);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const up = await axios.post(`${API}/upload/image`, fd, { headers: auth() });
        await axios.post(`${API}/admin/customer-photos`, { url: up.data.url }, { headers: auth() });
      } catch {
        toast.error(`Échec: ${file.name}`);
      }
      done += 1;
    }
    setUploading(0);
    if (files.length) toast.success(`${files.length} photo(s) ajoutée(s)`);
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette photo?')) return;
    await axios.delete(`${API}/admin/customer-photos/${id}`, { headers: auth() });
    setPhotos((p) => p.filter((x) => x.id !== id));
    toast.success('Photo supprimée');
  };

  const move = async (i, dir) => {
    const next = [...photos];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    setPhotos(next);
    await axios.put(`${API}/admin/customer-photos/order`, { ids: next.map((p) => p.id) }, { headers: auth() });
  };

  if (loading) return <AdminLayout><div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-blue-500" /></div></AdminLayout>;

  return (
    <AdminLayout>
    <div className="space-y-6" data-testid="admin-customer-photos">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Photos clients</h1>
          <p className="text-sm text-slate-500">Affichées sur « Nos clients satisfaits » sous « Nos livraisons récentes ». Tant qu'aucune photo n'est ajoutée, les véhicules vendus restent affichés.</p>
        </div>
        <div className="flex gap-2">
          <a href="/nos-livraisons" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-700 hover:bg-slate-100" data-testid="customer-photos-view-page">
            <ExternalLink className="h-4 w-4" /> Voir la page
          </a>
          <button onClick={() => inputRef.current?.click()} disabled={!!uploading} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-60" data-testid="customer-photos-upload-btn">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? `Envoi… (${uploading})` : 'Ajouter des photos'}
          </button>
          <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={onFiles} data-testid="customer-photos-file-input" />
        </div>
      </div>

      <PhotoSendersPanel />

      {photos.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 rounded-xl text-slate-500" data-testid="customer-photos-empty">Aucune photo client pour le moment.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {photos.map((p, i) => (
            <div key={p.id} className="relative group rounded-xl overflow-hidden border border-slate-200 bg-white" data-testid={`admin-customer-photo-${p.id}`}>
              <img src={thumb(p.url)} alt="" className="w-full aspect-[4/3] object-cover" />
              <div className="flex items-center justify-between p-2">
                <div className="flex gap-1 text-slate-600">
                  <button disabled={i === 0} onClick={() => move(i, -1)} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30" data-testid={`customer-photo-left-${p.id}`}><ArrowLeft className="h-4 w-4" /></button>
                  <button disabled={i === photos.length - 1} onClick={() => move(i, 1)} className="p-1.5 rounded hover:bg-slate-100 disabled:opacity-30" data-testid={`customer-photo-right-${p.id}`}><ArrowRight className="h-4 w-4" /></button>
                </div>
                <button onClick={() => remove(p.id)} className="p-1.5 rounded text-red-600 hover:bg-red-50" data-testid={`customer-photo-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </AdminLayout>
  );
};

export default AdminCustomerPhotos;
